import _ from 'lodash';
import PropTypes from 'prop-types';
import { useState } from 'react';
import constants from '../../../config/constants';
import { useCloseModalOnFlag } from '../../../utils/hooks/useDeliveryEffects';
import useDeliveryOptions from '../../../utils/hooks/useDeliveryOptions';
import { noop } from '../../../utils/noop';
import SideDrawer from '../../ui/SideDrawer/SideDrawer';
import {
  checkAvailability,
  getCommonZoneModalProps,
  getDeliveryEstimatesV2Data,
  getTypeSpecificProps,
  getZoneName,
} from './DeliveryOptions.Helper';
import { getHomeDeliveryData } from './HomeDelivery/HomeDelivery.Helper';
import ModalContentRenderer from './ModalContent/ModalContentRenderer';
import { getPickupInStoreData } from './PickupInStore/PickupInStore.Helper';
import { calculateHeightWidth } from './utils';

const { AVAILABILITY_PICKUPINSTORE, AVAILABILITY_STOCKINSTORE, AVAILABILITY_HOMEDELIVERY } = constants;

export const DeliveryOptionsModal = ({
  // Configuration
  appCtxData,
  labelsData,
  outOfStock,

  // Modal state
  modalVisible,
  setModalVisible,

  // Product data
  productData,
  variantData,
  cart,
  sizes,
  colorVariants,
  availabilityList,

  // Zone configuration
  zoneConfig,
  setZoneConfig,

  // Delivery options
  type,
  selectedType,
  setSelectedType,
  selectedDeliveryOption,
  setSelectedDeliveryOption,

  // Persistent delivery data
  deliveryData,
  deliveryActions,
  deliveryErrors,
}) => {
  const {
    deviceType,
    regionCode,
    isDefaultComuna,
    reloadOnZoneChange,
    store,
    endpoints,
    selectedZone,
    isHDExpressEnabled,
  } = appCtxData;

  const { offeringId } = variantData;

  // const [saveError, setSaveError] = useState(false);
  const [view, setView] = useState('map');
  const [shouldClose, setShouldClose] = useState(false);

  const { isStockInStore, isPickupInStore, isHomeDelivery } = {
    isStockInStore: selectedType === AVAILABILITY_STOCKINSTORE,
    isPickupInStore: selectedType === AVAILABILITY_PICKUPINSTORE,
    isHomeDelivery: selectedType === AVAILABILITY_HOMEDELIVERY,
  };

  const {
    screen,
    homeDeliveryData,
    selectedStoreId,
    selectedStoreName,
    isError,
    comunaName,
    isCambiar,
    storeData,
    parentStateName,
    isModalBusy,
    isMapView,
    setHomeDeliveryData,
    setPickupInStoreData,
    setIsMapView,
    setScreen,
    setSelectedStoreDetails,
    setSelectedStoreId,
    setSelectedStoreName,
    setComunaName,
    setIsCambiar,
    saveSelectedStoreOption,
    fetchStores,
    saveZones,
    setModalBusy,
    setParentStateName,
    setDeliveryError,
  } = useDeliveryOptions({
    type: selectedType,
    regionCode,
    isDefaultComuna,
    labelsData,
    modalVisible,
    zoneConfig,
    setZoneConfig,
    sizes,
    colorVariants,
    selectedDeliveryOption,
    setSelectedDeliveryOption,
    isSizeSelected: cart.isSizeSelected,
    deliveryData,
    deliveryActions,
    deliveryErrors,
    productData,
    variantData,
    outOfStock,
  });

  const { width, height } = calculateHeightWidth(deviceType, screen, selectedType, deliveryErrors);

  const getPickupInStoreSlotsData = (comuna, storePickUp) => {
    getPickupInStoreData({
      comuna,
      storePickUp,
      isDefaultComuna: _.isEmpty(zoneConfig),
      regionCode,
      store,
      zoneConfig,
      selectedDeliveryOption,
      isFreeShippingApplicable: variantData.isFreeShippingApplicable,
      setSelectedDeliveryOption,
      setPickupInStoreData,
      setDeliveryError,
      setComunaName,
      setSelectedStoreId: deliveryActions.setSelectedStoreId,
      setSelectedStoreDetails,
      setSelectedStoreName,
      setParentStateName,
      setIsMapView,
      outOfStock,
      labelsData,
      setTextGenerated: deliveryActions.setTextGenerated,
    });
  };

  const getHomeDeliverySlotsData = (comuna, homeDelivery) => {
    getHomeDeliveryData({
      regionCode,
      comuna,
      isDefaultComuna,
      isFreeShippingApplicable: variantData.isFreeShippingApplicable,
      homeDelivery,
      setComunaName,
      setHomeDeliveryData,
      setParentStateName,
      setDeliveryError,
      setIsMapView,
      setSelectedDeliveryOption,
      outOfStock,
      labelsData,
      setTextGenerated: deliveryActions.setTextGenerated,
      isHDExpressEnabled,
    });
  };

  const getDeliveryEstimatesV2SlotsData = (comuna, setBusy) => {
    getDeliveryEstimatesV2Data({
      comuna,
      setBusy,
      regionCode,
      store,
      offeringId,
      setParentStateName,
      getHomeDeliverySlotsData,
      getPickupInStoreSlotsData,
      setIsDataSetForMetric: deliveryActions.setIsDataSetForMetric,
      setScreen,
      handleChangedGeoData: deliveryActions.handleChangedGeoData,
    });
  };

  const getStockInStoreDataSlotsData = (comuna, setBusy) => {
    fetchStores({
      comuna,
      setBusy,
    });
  };
  const onCambiar = () => {
    setIsCambiar(true);
    setZoneConfig({});
    setScreen('screen2');
    setIsMapView(false);
    deliveryActions.clearDeliveryData({ type: selectedType, isCambiar: true });
  };

  useCloseModalOnFlag({
    shouldClose,
    setShouldClose,
    setModalVisible,
  });

  const hasActiveErrors = Object.values(isError).some((errorState) => errorState.isError);
  return (
    <div className="container">
      <SideDrawer
        toggleState={[modalVisible, setModalVisible]}
        width={width}
        height={height}
        heading
        modalBusy={isModalBusy}
        comunData={getZoneName({ ...selectedZone, ...zoneConfig }, isDefaultComuna)}
        availabilityList={availabilityList}
        type={type}
        selectedType={selectedType}
        screen={screen}
        view={view}
        isMapView={isMapView}
        onCambiar={onCambiar}
        setIsMapView={setIsMapView}
        setView={setView}
        setSelectedType={setSelectedType}
        isError={isError}
        deliveryActions={deliveryActions}
      >
        <ModalContentRenderer
          screen={screen}
          deviceType={deviceType}
          isHomeDelivery={isHomeDelivery}
          isPickupInStore={isPickupInStore}
          isStockInStore={isStockInStore}
          selectedType={selectedType}
          setIsMapView={setIsMapView}
          selectVariantProps={{
            cart,
            handleOnClose: () => setModalVisible(false),
            config: {
              title: '¿Cuál buscas?',
              iconClass: isHomeDelivery ? 'homeDelivery-icon' : 'pickupInStore-icon',
            },
            inDeliveryOptions: true,
            checkAvailability: () =>
              checkAvailability({
                isHomeDelivery,
                isPickupInStore,
                isStockInStore,
                zoneConfig,
                setScreen,
                homeDeliveryData,
                storeData,
              }),
          }}
          zoneModalProps={{
            ...getCommonZoneModalProps({
              deviceType,
              regionCode,
              reloadOnZoneChange,
              modalVisible,
              setModalVisible,
              store,
              zoneConfig,
              isDefaultComuna,
              selectedZone,
              isPickupInStore,
              isHomeDelivery,
              isStockInStoreFlag: isStockInStore,
              isError,
              saveZones,
              setScreen,
              getStockInStoreDataSlotsData,
              getDeliveryEstimatesV2SlotsData,
              setModalBusy,
              isCambiar,
            }),
            ...getTypeSpecificProps({
              type,
              zoneConfig,
              setModalVisible,
              comunaName,
              selectedStoreId,
              pickupInStoreData: storeData[AVAILABILITY_PICKUPINSTORE] || [],
              homeDeliveryData,
              setSelectedStoreId,
              setModalBusy,
            }),
          }}
          errorProps={{
            hasActiveErrors,
            isError,
            comunaName,
            parentStateName,
          }}
          mapProps={{
            type: selectedType,
            deviceType,
            labelsData,
            isStockInStoreFlow: selectedType === constants.AVAILABILITY_STOCKINSTORE,
            view,
            isMapView,
            storeData,
            selectedStoreId: deliveryData.selectedStoreId,
            zoneConfig,
            endpoints,
            actions: {
              setBusy: setModalBusy,
              close: () => {
                if (deviceType === 'mobile' && deliveryData.selectedStoreId[selectedType]) {
                  saveSelectedStoreOption();
                }
                setModalVisible(false);
              },
            },
            saveSelectedStoreOption,
            setSelectedStoreId: deliveryActions.setSelectedStoreId,
            onCambiar,
            deliveryData,
            deliveryActions,
          }}
          contentProps={{
            type: selectedType,
            data: homeDeliveryData,
            zoneConfig,
            setSelectedDeliveryOption,
            isFreeShippingApplicable: variantData.isFreeShippingApplicable,
            deviceType,
            selectedStoreName,
            setBusy: setModalBusy,
            setIsCambiar,
            isDefaultComuna,
          }}
        />
      </SideDrawer>
    </div>
  );
};

DeliveryOptionsModal.defaultProps = {
  setModalVisible: noop,
  productData: {},
  zoneConfig: {},
  setZoneConfig: noop,
  sizes: [],
  colorVariants: [],
  selectedDeliveryOption: {},
  setSelectedDeliveryOption: noop,
  deliveryData: {},
  deliveryActions: {},
  deliveryErrors: {
    PickupInStore: { isError: false, code: null },
    HomeDelivery: { isError: false, code: null },
    StockInStore: { isError: false, code: null },
  },
};

DeliveryOptionsModal.propTypes = {
  selectedType: PropTypes.string.isRequired,
  appCtxData: PropTypes.object.isRequired,
  modalVisible: PropTypes.bool.isRequired,
  setModalVisible: PropTypes.func,
  productData: PropTypes.object,
  zoneConfig: PropTypes.object,
  setZoneConfig: PropTypes.func,
  sizes: PropTypes.array,
  colorVariants: PropTypes.array,
  selectedDeliveryOption: PropTypes.object,
  setSelectedDeliveryOption: PropTypes.func,
  cart: PropTypes.object.isRequired,
  availabilityList: PropTypes.array.isRequired,
  deliveryData: PropTypes.object,
  deliveryActions: PropTypes.object,
  deliveryErrors: PropTypes.object,
  setSelectedType: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  variantData: PropTypes.object.isRequired,
  labelsData: PropTypes.object.isRequired,
  outOfStock: PropTypes.string.isRequired,
};
